import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, setApiToken, clearApiToken, getApiToken } from './api';
import { normalizeRoles } from '../utils/roles';
import { DEFAULT_AUTH_CONFIG } from './api/authConfig';

const IMPERSONATE_KEY = 'monksheets_impersonate_user';

interface User {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
  employeeType?: {
    id: number;
    name: string;
    totalHoursPerMonth: number | null;
    /**
     * MKS-327 — `'employee' | 'contractor'`, surfaced by GET /api/auth/me so
     * `/time` can stop deciding "is this a freelancer" by looking for the
     * substring "freelance" in the type's NAME.
     */
    category?: string | null;
  } | null;
  // Phase 46 follow-up (quick 260521-vct) — surfaced by GET /api/auth/me so the
  // v7 Preferences page (Surface 7) can render the field without a separate fetch.
  slackUserId?: string | null;
  // Phase 60 (Plan 60-03 / SMS-03) — Phone number surfaced by GET /api/auth/me
  // so the Preferences Phone Card can render the current value without a
  // separate fetch (column renamed in Phase 60 Plan 60-01).
  phoneNumber?: string | null;
  // Quick 260813-iqd — surfaced by GET /api/auth/me so the profile page's
  // PersonalEmailSection renders the current value without a separate fetch.
  personalEmail?: string | null;
  // F-22 (2026-05-13 UAT batch Phase 3) — backend now computes "am I a PM
  // of any non-deleted project" server-side. Replaces the prior pattern of
  // re-deriving from useMyProjects, which broke because the
  // /api/projects?memberOf=true response branch nulls `projectManager` on
  // sidebar rows and hard-filters state='in_progress' — see authController
  // getCurrentUser for the predicate.
  isPM?: boolean;
  hasLLMCondom?: boolean;
  /**
   * Phase 106 / D-09 — surfaced by `GET /api/auth/me`. A structurally
   * SEPARATE `User` column, deliberately NOT a `roles[]` member, so it can
   * never be derived from `user.roles` or granted through the admin
   * user-update surface. See `AuthContextValue.isSuperadmin` below for why
   * this flag is UX only.
   */
  isSuperadmin?: boolean;
  /**
   * Quick 260915-i0x — avatar color slot for the signed-in user, used by the
   * top-bar avatars which have no other DTO to read it from.
   */
  avatarColorSeq?: number | null;
  /**
   * MKS-203 — relative API path to the effective avatar (uploaded, else
   * synced Slack/Google photo), or null when there is none. The Avatar
   * component falls back to initials unless this is a non-empty string.
   */
  avatarUrl?: string | null;
  /** MKS-203 — whether the effective avatar is a user upload (vs. synced). */
  hasAvatarUpload?: boolean;
}

export interface ImpersonatedUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (loginPath?: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setAuthToken: (token: string) => void;
  hasRole: (role: string) => boolean;
  isAdmin: boolean;
  isManager: boolean;
  isTaskManager: boolean;
  isHR: boolean;            // RENAMED from isHappyMaker (D-06)
  isFinance: boolean;       // NEW (D-04)
  /**
   * quick-260813-hob: raw `finance` role, admin NOT OR'd in — deliberately.
   * Mirrors the backend `requireFinanceOnly` gate on the monthly-salary
   * routes. `isFinance` stays the loose (admin-or-finance) flag for every
   * other finance surface; use this ONLY for the salary-amount surface.
   */
  isFinanceOnly: boolean;
  isMarketing: boolean;     // NEW (D-04)
  isISMSManager: boolean;   // NEW (D-04)
  isClientManager: boolean; // NEW (D-04)
  isSales: boolean;
  canManageTasks: boolean;
  /**
   * F-22 (2026-05-13 UAT batch Phase 3) — true when the resolved user is
   * `projectManagerId` on at least one non-deleted project. Backed by
   * `User.isPM` from GET /api/auth/me (single source of truth, see
   * authController.getCurrentUser). During impersonation the backend
   * `impersonate` middleware swaps `request.currentUser` before computing
   * `isPM`, so this flag mirrors the impersonated persona automatically —
   * admin acting as a PM sees PM surfaces; admin acting as a non-PM sees
   * them hidden.
   */
  isPM: boolean;
  /**
   * Phase 106 / D-01 / D-09 — deliberately NOT OR'd with `isAdmin`, unlike
   * every other flag in this interface. Superadmin must be a strictly
   * narrower gate than "admin of this instance" (D-01), and the field it is
   * derived from is a separate `User` column, not a `roles[]` member. This
   * flag is UX only — the real enforcement boundary is the backend's
   * `requireSuperadmin` middleware on `/api/fleet/*`.
   */
  isSuperadmin: boolean;
  impersonatedUser: ImpersonatedUser | null;
  isImpersonating: boolean;
  startImpersonating: (user: ImpersonatedUser) => void;
  stopImpersonating: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function buildDevUser(role: string): User {
  // Phase 106 / D-09 — deliberately no `isSuperadmin` here. This bypass
  // builds a user from a `roles[]` string, and superadmin is not a role
  // (it is a separate `User` column) — do not "complete" this later.
  const isFreelance = role === 'freelance';
  const validRoles = ['admin', 'manager', 'task_manager', 'client_manager', 'finance', 'hr', 'sales', 'marketing', 'isms_manager', 'user'] as const;
  const roles: string[] = isFreelance
    ? ['user']
    : (validRoles as readonly string[]).includes(role) ? [role] : [];
  return {
    id: 9999,
    email: `dev-${role}@topmonks.local`,
    firstName: 'Dev',
    lastName: role.charAt(0).toUpperCase() + role.slice(1),
    roles,
    employeeType: {
      id: isFreelance ? 2 : 1,
      name: isFreelance ? 'Freelance' : 'Full-time',
      totalHoursPerMonth: 160,
      category: isFreelance ? 'contractor' : 'employee',
    },
  };
}

function loadImpersonatedUser(): ImpersonatedUser | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonatedUser] = useState<ImpersonatedUser | null>(
    loadImpersonatedUser
  );
  const navigate = useNavigate();

  const fetchUser = async () => {
    // Dev-only auth stub. Set VITE_DEV_USER in .env.local to one of:
    // admin | manager | task_manager | client_manager | finance | hr | sales | marketing | isms_manager | user | freelance
    // (freelance maps to user role + Freelance employee type)
    const devUser = import.meta.env.DEV ? import.meta.env.VITE_DEV_USER : undefined;
    if (devUser) {
      setUser(buildDevUser(devUser));
      setIsLoading(false);
      return;
    }

    // Check if we have any auth signal: localStorage token OR auth_logged_in cookie.
    // The cookie survives browser privacy cleanups that clear localStorage.
    const hasToken = !!getApiToken();
    const hasCookie = document.cookie.includes('auth_logged_in=1');
    if (!hasToken && !hasCookie) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get<{ data: User }>('/auth/me');
      // Normalize roles from Rails-style to our expected format
      const userData = response.data;
      userData.roles = normalizeRoles(userData.roles);
      setUser(userData);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        // Token is invalid — clear both localStorage token and the indicator
        // cookie so the hasCookie check doesn't re-trigger fetchUser on reload
        clearApiToken();
        setUser(null);
      } else {
        console.error('Failed to fetch user:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  // Listen for auth:expired events dispatched by api.ts / api/client.ts when any
  // request receives a 401. This clears the in-memory user state immediately so
  // ProtectedRoute can redirect to /login without requiring a page reload.
  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(null);
      navigate('/login');
    };
    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, [navigate]);

  const login = (loginPath: string = DEFAULT_AUTH_CONFIG.loginPath) => {
    // Redirect to the backend-reported provider login route. The caller
    // resolves loginPath from the backend-reported provider config
    // (see services/api/authConfig.ts), defaulting to the Google route.
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    // Re-guard here even though authConfig.ts already validates the shape:
    // login() must stay safe on its own if a future caller ever passes
    // through unvalidated data. WR-04 (Phase 95 code review): also reject a
    // second leading `/` (protocol-relative `//host/...`) — `startsWith('/')`
    // alone admits it, and a future caller that assigns
    // `window.location.href = loginPath` directly (without this `apiUrl`
    // prefix) would otherwise reintroduce a classic open redirect.
    const safePath =
      typeof loginPath === 'string' && loginPath.startsWith('/') && !loginPath.startsWith('//')
        ? loginPath
        : DEFAULT_AUTH_CONFIG.loginPath;
    window.location.href = `${apiUrl}${safePath}`;
  };

  const stopImpersonating = () => {
    sessionStorage.removeItem(IMPERSONATE_KEY);
    window.location.href = '/';
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      stopImpersonating();
      clearApiToken();
      setUser(null);
      navigate('/login');
    }
  };

  const setAuthToken = (token: string) => {
    setApiToken(token);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const hasRole = (role: string): boolean => {
    return user?.roles.includes(role) ?? false;
  };

  // Per D-13 (Phase 27), FE has always derived flags from the real user
  // (impersonation only stores impersonatedUser; user.roles is never overwritten).
  // The backend rbac.ts now mirrors this via getActorRoles(request.realUser ?? request.currentUser).
  const isAdmin = hasRole('admin');
  const isManager = isAdmin || hasRole('manager');
  const isTaskManager = hasRole('task_manager');
  const isHR = isAdmin || hasRole('hr');                        // RENAMED + role string changed (D-06)
  const isFinance = isAdmin || hasRole('finance');              // NEW (D-04)
  // quick-260813-hob: strict, admin NOT OR'd in — mirrors requireFinanceOnly.
  const isFinanceOnly = hasRole('finance');
  const isMarketing = isAdmin || hasRole('marketing');          // NEW (D-04)
  const isISMSManager = isAdmin || hasRole('isms_manager');     // NEW (D-04)
  const isClientManager = isAdmin || hasRole('client_manager'); // NEW (D-04)
  const isSales = isAdmin || hasRole('sales');
  const canManageTasks = isManager || isTaskManager;
  // F-22 — see User.isPM doc. Backend GET /api/auth/me runs through the
  // `impersonate` middleware, so during impersonation `user` is the target
  // user's payload (including the target's `isPM` flag). Reading
  // `user?.isPM` therefore already reflects the impersonated persona — no
  // need to AND with `!isImpersonating` (the prior pattern incorrectly hid
  // PM surfaces while admin was acting as a real PM, breaking persona UAT).
  const isImpersonating = !!impersonatedUser;
  const isPM = user?.isPM ?? false;
  // Phase 106 / D-09 — deliberately NOT OR'd with isAdmin. See
  // AuthContextValue.isSuperadmin's doc comment above.
  const isSuperadmin = user?.isSuperadmin ?? false;

  const startImpersonating = (targetUser: ImpersonatedUser) => {
    sessionStorage.setItem(IMPERSONATE_KEY, JSON.stringify(targetUser));
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
        setAuthToken,
        hasRole,
        isAdmin,
        isManager,
        isTaskManager,
        isHR,
        isFinance,
        isFinanceOnly,
        isMarketing,
        isISMSManager,
        isClientManager,
        isSales,
        canManageTasks,
        isPM,
        isSuperadmin,
        impersonatedUser,
        isImpersonating,
        startImpersonating,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Like {@link useAuth}, but returns `null` instead of throwing when no
 * AuthProvider is in the tree. Use this for gates that should fail closed
 * (no auth → no permission) without forcing every render path / test to
 * wrap in a provider.
 */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext);
}

// Helper hook for getting full name
export function useUserDisplayName(): string {
  const { user } = useAuth();
  if (!user) return '';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email;
}
